import type {
  CartFragment,
  ConfigurableProductDetailsFragment,
  SimpleProduct,
  SimpleProductDetailsFragment,
} from '../graphql/magento'
import { CartItemVirtualProduct } from '../lib/cart/state/types'

export const formatAsCurrency = ({
  amount,
  fractionDigits,
  ignoreFalsyAmount,
}: {
  amount: number
  fractionDigits?: number | undefined
  ignoreFalsyAmount?: boolean
}): string => {
  const digitsWithDefault = typeof fractionDigits === 'number' ? fractionDigits : 2

  if (ignoreFalsyAmount ? Number(amount) : !isNaN(Number(amount))) {
    const amountSign = Math.sign(amount)
    if (amountSign === -1) {
      return `-${`$${Number(Math.abs(amount)).toFixed(digitsWithDefault)}`}`
    }

    return `$${Number(amount).toFixed(digitsWithDefault)}`
  }

  return ''
}

export const formatAsCurrencyWithCommas = ({
  amount,
  fractionDigits,
}: {
  amount: number
  fractionDigits?: number | undefined
}): string => {
  const digitsWithDefault = typeof fractionDigits === 'number' ? fractionDigits : 2

  if (Number(amount)) {
    const amountSign = Math.sign(amount)
    const formattedAmount = Number(Math.abs(amount))
      .toFixed(digitsWithDefault)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    if (amountSign === -1) {
      return `-$${formattedAmount}`
    }

    return `$${formattedAmount}`
  }

  return ''
}

export const formatAsCurrencyOrFree = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  if (amountSign === -1) {
    return `-${`$${Number(Math.abs(amount)).toFixed(2)}`}`
  }

  if (amountSign === 0 || !amount) {
    return 'Free'
  }

  return `$${Number(amount).toFixed(2)}`
}

export const formatAsCurrencyOrZero = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  if (amountSign === -1) {
    return `-${`$${Number(Math.abs(amount)).toFixed(2)}`}`
  }

  if (amountSign === 0 || !amount) {
    return '$0.00'
  }

  return `$${Number(amount).toFixed(2)}`
}

export const formatAsCurrencyWithCommasOrZero = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  const formattedAmount = Number(Math.abs(amount))
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  if (amountSign === -1) {
    return `-$${formattedAmount}`
  }

  if (amountSign === 0 || !amount) {
    return '$0.00'
  }

  return `$${formattedAmount}`
}

export const dropZeroCents = (amount: string): string => {
  if (amount.endsWith('.00')) {
    return amount.slice(0, -3)
  }
  return amount
}

export const formatAsCurrencyOrEmpty = ({ amount }: { amount: number }): string => {
  const amountSign = Math.sign(amount)

  if (amountSign === -1) {
    return `-${`$${Number(Math.abs(amount)).toFixed(2)}`}`
  }

  if (amountSign === 0 || !amountSign) {
    return '–'
  }

  return `$${Number(amount).toFixed(2)}`
}

export const rawItemPrice = ({ price, qty }: { price: number; qty: number }): number => price * qty

export const itemQuantityPrice = ({
  item,
  qty,
  ignoreFalsyAmount,
}: {
  item: Pick<SimpleProduct, '__typename' | 'price_tiers'> | undefined | null
  qty: number
  ignoreFalsyAmount?: boolean
}): string => {
  const price =
    item?.__typename === 'SimpleProduct' ? item.price_tiers?.[0]?.final_price?.value || 0 : 0

  return formatAsCurrency({ amount: rawItemPrice({ price, qty }), ignoreFalsyAmount })
}

export const itemPrice = ({
  item,
  ignoreFalsyAmount,
}: {
  item: ConfigurableProductDetailsFragment | SimpleProductDetailsFragment | undefined | null
  ignoreFalsyAmount?: boolean
}): string =>
  formatAsCurrency({
    amount:
      item?.__typename === 'SimpleProduct' && 'price_tiers' in item
        ? item.price_tiers?.[0]?.final_price?.value || 0
        : 0,
    ignoreFalsyAmount,
  })

// TODO: Update to use proper price ranges
// export const itemPriceRangeOrPrice = ({ item }) => {
//   const maximumPrice = item?.price_range?.maximum_price?.regular_price?.value

//   const minimumPrice = item?.price_range?.minimum_price?.regular_price?.value

//   if (minimumPrice && maximumPrice) {
//     return maximumPrice === minimumPrice
//       ? formatAsCurrency({ amount: minimumPrice })
//       : `${formatAsCurrency({ amount: minimumPrice })} - ${formatAsCurrency({
//           amount: maximumPrice,
//         })}`
//   }

//   if (maximumPrice) {
//     return formatAsCurrency({ amount: maximumPrice })
//   }

//   if (minimumPrice) {
//     return formatAsCurrency({ amount: minimumPrice })
//   }

//   return ''
// }

export const itemDetailsPriceDisplay = ({
  selectedVariant,
  currentQty,
  ignoreFalsyAmount,
}: {
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  currentQty: { value: string }
  ignoreFalsyAmount?: boolean
}): string => {
  const price = itemQuantityPrice({
    item: selectedVariant,
    qty: Number(currentQty.value),
    ignoreFalsyAmount,
  })

  return price ? `- ${price}` : ''
}

export const isItemPriceZero = (
  selectedVariant: SimpleProductDetailsFragment | null | undefined,
): boolean =>
  !rawItemPrice({
    price:
      selectedVariant?.__typename === 'SimpleProduct'
        ? selectedVariant.price_tiers?.[0]?.final_price?.value || 0
        : 0,
    qty: 1,
  })

// CART Helpers

export const cartSubtotal = ({ items, prices }: Pick<CartFragment, 'items' | 'prices'>): string => {
  if (items?.length) {
    return formatAsCurrency({ amount: prices?.subtotal_excluding_tax?.value || 0 })
  }

  return formatAsCurrencyOrEmpty({ amount: 0 })
}

export const shippingTotal = ({
  shippingAddresses,
  items,
}: {
  shippingAddresses: CartFragment['shipping_addresses']
  items: CartFragment['items']
}): string => {
  if (shippingAddresses.length && shippingAddresses[0]?.selected_shipping_method && items?.length) {
    return formatAsCurrencyOrFree({
      amount: shippingAddresses[0].selected_shipping_method.amount.value || 0,
    })
  }

  return '–'
}

export const salesTax = ({ prices }: Pick<CartFragment, 'prices'>): string => {
  const amount = (prices?.applied_taxes || []).reduce<number>(
    (acc, appliedTax) => acc + (appliedTax?.amount?.value || 0),
    0,
  )
  return formatAsCurrencyOrEmpty({ amount })
}

export const taxType = ({ prices }: Pick<CartFragment, 'prices'>): string => {
  const [appliedTax] = prices?.applied_taxes || []
  if (!appliedTax?.label || appliedTax.label === 'Tax') {
    return ''
  }
  return ` (${appliedTax.label})`
}

export const discountAmount = ({
  appliedCoupons,
  prices,
}: {
  appliedCoupons: CartFragment['applied_coupons']
  prices: CartFragment['prices']
}): string => {
  if (appliedCoupons == null || !appliedCoupons.length) {
    return formatAsCurrencyOrEmpty({ amount: 0 })
  }

  const amount = (prices?.discounts || []).reduce<number>(
    (acc, discount) => acc + (discount?.amount.value || 0),
    0,
  )
  return formatAsCurrencyOrEmpty({
    amount,
  })
}

export const rebateAmount = (cart: CartFragment): string | null =>
  cart.rebate?.value ? formatAsCurrency({ amount: -Math.abs(cart.rebate.value) }) : null

export const cartTotal = ({ cart }: { cart: CartFragment }): string =>
  formatAsCurrencyOrZero({ amount: cart.prices?.grand_total?.value || 0 })

export const cartItemPrice = ({
  item,
}: {
  item: Exclude<CartFragment['items'], undefined | null>[0]
}): string =>
  formatAsCurrency({
    amount: item?.prices?.row_total.value || 0,
  })

export const cartItemAndDesignFeePrice = (
  item: Exclude<CartFragment['items'], undefined | null>[number],
  designFeeItem: CartItemVirtualProduct | undefined,
): string =>
  formatAsCurrency({
    amount: (item?.prices?.row_total.value || 0) + (designFeeItem?.prices?.row_total.value || 0),
  })

export const coloradoDeliveryFee = (cart: CartFragment): string | null => {
  const amount = cart.prices?.colorado_retail_delivery_fee?.value
  return amount ? formatAsCurrency({ amount }) : null
}
